import {
  Area,
  CartesianGrid,
  Cell,
  ComposedChart,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Box, Paper } from '@mui/material';
import React, { useEffect, useState } from 'react';
import CircleIcon from '@mui/icons-material/Circle';
import Grid from '@mui/material/Unstable_Grid2';
import { useResizeDetector } from 'react-resize-detector';
import { isSameDay } from 'date-fns';
import {
  AgeType,
  BMIType,
  DayList02,
  GenderType,
  GenderType02,
  MedalType,
  WalkPeriodType,
} from '../SwDefaultValues';
import { LoadingPage } from '../LoadingPage';
import { GraphColors } from '../../../common/helper';

interface Props {
  title?: string;
  lgNumber?: number;
  xsNumber?: number;
  tooltipText?: any;
  data?: any[];
  from?: string;
  dateInfo?: string;
  showValue?: boolean;
  showTooltip?: boolean;
  rangeDate?: any;
  isToggle?: boolean;
  toggleType?: { type: string; value: 1 | 2 };
  onChangeToggle?: (type: string, newValue: 1 | 2) => void;
  loading?: boolean;
}

const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    return (
      <Box className='custom-tooltip'>
        <p className='label'>{`${payload[0].name} : ${payload[0].value}`}</p>
      </Box>
    );
  }
  return null;
};

const renderCustomLegendText = (value: string, entry: any) => {
  const { color } = entry;
  const { payload } = entry;
  return (
    <div style={{ display: 'inline-block' }}>
      {value}
      <span style={{ marginLeft: '18px' }}>({payload.value})</span>
    </div>
  );
};

export const SwCircleGraph: React.FC<Props> = ({
  title,
  lgNumber = 4,
  xsNumber = 6,
  tooltipText,
  data,
  from,
  dateInfo,
  showValue = true,
  showTooltip = true,
  rangeDate,
  toggleType,
  onChangeToggle,
  loading,
}) => {
  const [lclData, setLclData] = useState<any[]>([]);
  const [isToday, setIsToday] = useState(false);
  const { width, ref } = useResizeDetector();
  const [type, setType] = useState<any>();
  const [useAvg, setUseAvg] = useState<boolean>(false);

  useEffect(() => {
    if (rangeDate && rangeDate[1]) {
      setIsToday(isSameDay(new Date(), new Date(rangeDate[1])));
    }
  }, [rangeDate]);

  useEffect(() => {
    setUseAvg(!!title?.includes('읽기'));
  }, [title]);

  useEffect(() => {
    if (data && data.length > 0) {
      if (from === 'medal') {
        let count = 0;
        let avgCount = 0;
        data.map((item: any) => {
          count += item.value;
          avgCount += item.medal !== 'empty' ? item.avg : 0;
        });
        if (count === 0 || avgCount === 0) setLclData([]);
        else setLclData(data);
      } else {
        setLclData(data);
      }
    } else {
      setLclData([]);
    }
  }, [data]);

  useEffect(() => {
    const newType: any =
      from === 'gender'
        ? GenderType
        : from === 'gender02'
        ? GenderType02
        : from === 'age'
        ? AgeType
        : from === 'bmi'
        ? BMIType
        : from === 'dayOfWeek'
        ? DayList02
        : from === 'medal'
        ? MedalType
        : WalkPeriodType;
    setType(newType);
  }, []);

  return (
    <>
      <Box>
        <div
          className='dashboard_graph_section'
          style={{ backgroundColor: '#fff' }}
        >
          {loading ? (
            <Box sx={{ width: width, height: '308px', paddingTop: 0 }}>
              <LoadingPage />
            </Box>
          ) : lclData && lclData.length > 0 ? (
            <div
              className='dashboard_graph'
              style={{ width: width, height: '300px', position: 'relative' }}
            >
              <ResponsiveContainer width='100%' height='100%'>
                <PieChart
                  margin={{
                    left: -10,
                    bottom: 5,
                  }}
                >
                  <Pie
                    data={lclData}
                    cx='30%'
                    cy='50%'
                    innerRadius={80}
                    outerRadius={110}
                    fill='#8884d8'
                    paddingAngle={2}
                    dataKey={useAvg ? 'avg' : 'sum'}
                    legendType='circle'
                    // cornerRadius={100}
                    stroke='none'
                    isAnimationActive={false}
                  >
                    {lclData.map((d: any, index: number) => {
                      const newFrom = from === 'gender02' ? 'gender' : from;
                      const newVal =
                        newFrom &&
                        type?.findIndex((q: any) => q.value === d[newFrom]);
                      return (
                        <Cell
                          key={`cell-${newFrom}`}
                          fill={GraphColors[newVal]}
                        />
                      );
                    })}
                  </Pie>
                  <Tooltip
                    // eslint-disable-next-line react/no-unstable-nested-components
                    content={({ active, payload, label }: any) => {
                      if (active && payload && payload.length && showTooltip) {
                        const newFrom = from === 'gender02' ? 'gender' : from;
                        const name =
                          newFrom &&
                          type.filter(
                            (t: any) => t.value === payload[0].payload[newFrom]
                          ).length > 0
                            ? type.filter(
                                (t: any) =>
                                  t.value === payload[0].payload[newFrom]
                              )
                            : [{ name: 'temp' }];
                        return (
                          <div style={{ position: 'relative' }}>
                            <Box
                              className='graph_tooltip_box'
                              sx={{ zIndex: 100 }}
                            >
                              <p className='graph_tooltip_title'>
                                {name[0].name}
                              </p>
                              <p className='avg'>{payload[0].value}%</p>
                            </Box>
                          </div>
                        );
                      }
                      return null;
                    }}
                  />
                  {/* <Tooltip /> */}
                </PieChart>
              </ResponsiveContainer>
              <Box
                sx={{
                  position: 'absolute',
                  right: width && width > 398 ? width && width / 6 : '2%',
                  top: '50%',
                }}
              >
                {type?.map((t: any, idx: number) => {
                  const newFrom = from === 'gender02' ? 'gender' : from;
                  const newValue = lclData.filter(
                    (d: any) => newFrom && d[newFrom] === t.value
                  );
                  const totalSum = lclData?.reduce(
                    (q: any, w: any) => q + w.value,
                    0
                  );
                  return (
                    <Box
                      key={idx.toString()}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          pr: '15px',
                        }}
                      >
                        <CircleIcon
                          sx={{
                            fontSize: '15px',
                            marginRight: '5px',
                            color: GraphColors[idx],
                          }}
                        />
                        <Box className='circle_legend'>{t.name}</Box>
                      </Box>
                      {t.name !== '메달없음' ? (
                        <Box className='circle_legend'>
                          {`${
                            newValue.length
                              ? useAvg
                                ? newValue[0].avg
                                : newValue[0].sum
                              : 0
                          }%`}
                          {showValue && '('}
                          {showValue && newValue.length
                            ? newValue[0].value
                            : showValue && '0'}
                          {showValue && (title?.includes('읽기') ? '회' : '회')}
                          {showValue && ')'}
                        </Box>
                      ) : (
                        <Box className='circle_legend'>
                          {`${(useAvg
                            ? newValue[0].avg
                            : newValue[0].sum
                          ).toFixed(1)}%`}
                          {showValue && '('}
                          {showValue && newValue.length
                            ? newValue[0].value
                            : showValue && '0'}
                          {showValue && (title?.includes('읽기') ? '회' : '회')}
                          {showValue && ')'}
                        </Box>
                      )}
                    </Box>
                  );
                })}
              </Box>
            </div>
          ) : (
            <Box
              className='no-data'
              sx={{ width: width, height: '300px', paddingTop: 0 }}
            >
              <div>
                {isToday
                  ? '오늘 날짜를 포함하여 조회시\n데이터가 표시되지 않습니다.'
                  : '데이터가 없습니다.'}
              </div>
            </Box>
          )}
        </div>
      </Box>
    </>
  );
};
